/**
 * Экран «Прогресс» (раздел 8, пункт 3).
 *
 * Три блока одного экрана: расчётный максимум с графиком и прогнозом по
 * выбранному движению, недельный объём по мышечным группам и вес тела.
 *
 * Ни одно число здесь не считается (раздел 2, принцип 1). e1RM, темп,
 * регрессия, коридор объёма и сглаженный вес приходят из движка через
 * `backend/routers/progress.py`. Клиент выбирает, форматирует и раскладывает
 * по пикселям — если в этом файле появится арифметика над килограммами или
 * процентами, это ошибка слоя.
 *
 * Честность (раздел 2, принцип 3) здесь дороже красоты: прогноза нет —
 * показываем причину сервера текстом и не рисуем никакой даты; темпа веса
 * нет — так и пишем, вместо нуля, который читался бы как «вес стоит».
 */

import { useMemo, useState } from 'react'

import { api, NetworkError } from '../api/client'
import type {
  Flag,
  Progress as ProgressData,
  Today,
  VolumeReport,
  VolumeWeek,
} from '../api/types'
import { haptic } from '../auth/telegram'
import Chart from '../components/Chart'
import { Empty, ErrorBox, Flags, Loader, formatDate, formatWeight } from '../components/ui'
import { useAsync } from '../hooks'
import { MUSCLE_RU } from '../labels'

/**
 * Ответ GET /progress/bodyweight (BodyweightOut в backend/routers/progress.py).
 *
 * В общих типах его нет: ручка вспомогательная и нужна только этому экрану.
 * `points` — уже сглаженный сервером ряд, `rate_*` при has_trend = false
 * равны нулю в значении «неизвестно», а не «не меняется».
 */
interface BodyweightPoint {
  date: string
  weight_kg: number
}

interface BodyweightReport {
  points: BodyweightPoint[]
  current_avg: number | null
  previous_avg: number | null
  rate_kg_per_week: number
  rate_pct_per_week: number
  has_trend: boolean
  n_points: number
  span_days: number
  smoothing_days: number
  flags: Flag[]
}

/** Неделя объёма отдаёт ещё и `is_deload` — в общих типах поля нет. */
type VolumeWeekFull = VolumeWeek & { is_deload?: boolean }
type VolumeReportFull = Omit<VolumeReport, 'weeks'> & { weeks: VolumeWeekFull[] }

/**
 * Куда смотреть, когда тренировки на сегодня нет.
 *
 * Жим лёжа — целевое движение пользователя (см. engine/catalog.py, id = 1):
 * открывать экран прогресса пустым, пока не наступит день тренировки, —
 * ровно то, чего требует избежать раздел 2.
 */
const FALLBACK_EXERCISE_ID = 1
const FALLBACK_EXERCISE_NAME = 'Жим лёжа'

interface ExerciseOption {
  id: number
  name: string
}

/** Дробное значение без хвостовых нулей: 12, 12.5. Только показ, не счёт. */
function formatNumber(value: number, digits = 1): string {
  if (Number.isInteger(value)) return String(value)
  return value.toFixed(digits).replace(/0+$/, '').replace(/\.$/, '')
}

/** Темп со знаком: рост и снижение должны различаться с одного взгляда. */
function formatRate(value: number, digits = 2): string {
  const text = formatNumber(value, digits)
  return value > 0 ? `+${text}` : text
}

/**
 * Дата прогноза — обязательно с годом, когда он не текущий.
 *
 * `formatDate` даёт «14 марта». До жима 100 кг с текущих 60-70 идти дольше
 * года, и дата без года читается как ближайшая весна: экран показал бы срок
 * ближе настоящего (раздел 2, принцип 3; раздел 13 — «прогноз врёт»). Тот же
 * приём, что у `formatGoalDate` на экране «Цели».
 */
function formatForecastDate(iso: string): string {
  const base = formatDate(iso)
  // «сегодня», «вчера», «завтра» — в них года нет по смыслу. Относительные
  // ответы `formatDate` отличаются тем, что в них нет ни одной цифры.
  if (!/\d/.test(base)) return base
  const year = Number(iso.slice(0, 4))
  return Number.isFinite(year) && year !== new Date().getFullYear() ? `${base} ${year}` : base
}

/**
 * Начало недели — абсолютной датой.
 *
 * `formatDate` в понедельник и вторник отдаёт «сегодня»/«вчера», и заголовок
 * превращается в «Неделя с сегодня». Здесь нужна именно дата.
 */
function weekStartLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

/**
 * Ошибка блока.
 *
 * NetworkError и ApiError различаются намеренно: «нет связи» на этом экране
 * означает, что показывать нечего вообще — в отличие от «Сегодня», прогресс
 * не кешируется, потому что это всегда свежий счёт сервера.
 */
function SectionError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <>
      <ErrorBox error={error} onRetry={onRetry} />
      {error instanceof NetworkError && (
        <div className="faint" style={{ margin: '-4px 0 12px' }}>
          Прогресс считается на сервере, локальной копии нет — без связи цифр не будет.
        </div>
      )}
    </>
  )
}

export default function Progress() {
  // Список упражнений берём из сегодняшней тренировки: /sessions отдаёт сессии
  // без блоков, а значит и без упражнений — по нему ленту не построить.
  const today = useAsync(() => api.get<Today>('/today'), [])
  const [picked, setPicked] = useState<number | null>(null)

  const blocks = today.data?.session?.blocks
  const options = useMemo<ExerciseOption[]>(() => {
    const list: ExerciseOption[] = []
    const seen = new Set<number>()
    for (const block of blocks ?? []) {
      if (seen.has(block.exercise.id)) continue
      seen.add(block.exercise.id)
      list.push({ id: block.exercise.id, name: block.exercise.name })
    }
    if (list.length === 0) list.push({ id: FALLBACK_EXERCISE_ID, name: FALLBACK_EXERCISE_NAME })
    return list
  }, [blocks])

  const selectedId = picked ?? options[0]?.id ?? FALLBACK_EXERCISE_ID

  // Пока /today не ответил, упражнение ещё не выбрано. Запрашивать прогресс
  // наугад — лишний запрос и мигание чужими цифрами на экране.
  const exerciseId = today.loading ? null : selectedId
  const progress = useAsync<ProgressData | null>(
    () => (exerciseId === null ? Promise.resolve(null) : api.get<ProgressData>(`/progress/${exerciseId}`)),
    [exerciseId],
  )

  const volume = useAsync(() => api.get<VolumeReportFull>('/progress/volume'), [])
  const bodyweight = useAsync(() => api.get<BodyweightReport>('/progress/bodyweight'), [])

  const usingFallback = (blocks?.length ?? 0) === 0
  // Имя движения принадлежит справочнику сервера: у запасного варианта оно
  // уточняется, как только ответил /progress («Жим лёжа» -> «Жим лёжа со
  // штангой»). Своё название клиент показывает только до первого ответа.
  const named = progress.data?.exercise

  return (
    <div className="screen">
      <h1>Прогресс</h1>

      {/* --------------------------------------------------------------- */}
      {/* 1. Выбор движения                                               */}
      {/* --------------------------------------------------------------- */}
      {/* Ленту рисуем только после ответа /today: до него список ещё не
          известен, и единственная «таблетка» запасного движения мигнула бы
          названием, которого в сегодняшней тренировке может не быть. */}
      {!today.loading && (
        <div className="picker" role="tablist" aria-label="Упражнение">
          {options.map((option) => {
            const active = option.id === selectedId
            return (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={active ? 'pill-tab active' : 'pill-tab'}
                onClick={() => {
                  haptic('light')
                  setPicked(option.id)
                }}
              >
                {named && named.id === option.id ? named.name : option.name}
              </button>
            )
          })}
        </div>
      )}

      {usingFallback && !today.loading && (
        <div className="faint" style={{ marginBottom: 12 }}>
          {today.error
            ? 'Список сегодняшних упражнений не загрузился — показываю жим лёжа, твоё целевое движение.'
            : 'Тренировки на сегодня нет — показываю жим лёжа, твоё целевое движение.'}
        </div>
      )}

      {/* --------------------------------------------------------------- */}
      {/* 2. Расчётный максимум, график, прогноз                          */}
      {/* --------------------------------------------------------------- */}
      {today.loading || progress.loading ? (
        <Loader label="Считаю максимум…" />
      ) : progress.error ? (
        <SectionError error={progress.error} onRetry={() => void progress.reload()} />
      ) : progress.data === null ? null : (
        <ExerciseProgress data={progress.data} />
      )}

      {/* --------------------------------------------------------------- */}
      {/* 3. Недельный объём                                              */}
      {/* --------------------------------------------------------------- */}
      <h2>Недельный объём</h2>
      {volume.loading ? (
        <Loader label="Собираю объём…" />
      ) : volume.error ? (
        <SectionError error={volume.error} onRetry={() => void volume.reload()} />
      ) : volume.data ? (
        <VolumeBlock report={volume.data} />
      ) : null}

      {/* --------------------------------------------------------------- */}
      {/* 4. Вес тела                                                     */}
      {/* --------------------------------------------------------------- */}
      <h2>Вес тела</h2>
      {bodyweight.loading ? (
        <Loader label="Смотрю взвешивания…" />
      ) : bodyweight.error ? (
        <SectionError error={bodyweight.error} onRetry={() => void bodyweight.reload()} />
      ) : bodyweight.data ? (
        <BodyweightBlock report={bodyweight.data} />
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Расчётный максимум по выбранному движению                                 */
/* ------------------------------------------------------------------------- */

function ExerciseProgress({ data }: { data: ProgressData }) {
  const forecast = data.forecast

  if (data.points.length === 0 && data.current_e1rm === null) {
    return (
      <>
        <Empty
          title={`${data.exercise.name}: подходов ещё нет`}
          hint="Запиши тренировку с этим движением — сервер посчитает расчётный максимум и построит график."
        />
        <Flags flags={data.flags} />
      </>
    )
  }

  return (
    <>
      <div className="card">
        <div className="row between" style={{ alignItems: 'flex-end' }}>
          <div>
            <div className="faint">расчётный максимум</div>
            <div className="big-number">
              {formatWeight(data.current_e1rm)}
              <span style={{ fontSize: 18, fontWeight: 600 }}> кг</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="faint">лучший</div>
            <div className="tnum" style={{ fontSize: 19, fontWeight: 600 }}>
              {formatWeight(data.best_e1rm)} кг
            </div>
          </div>
        </div>

        {data.points.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Chart
              points={data.points.map((point) => ({ date: point.date, value: point.e1rm }))}
              height={180}
              // Пунктир и линия цели рисуются ТОЛЬКО при has_forecast = true:
              // на плохих данных прогноза не существует (раздел 5.5).
              forecast={
                forecast?.has_forecast && forecast.projected_date
                  ? { date: forecast.projected_date, value: forecast.target }
                  : undefined
              }
              target={forecast?.has_forecast ? forecast.target : undefined}
            />
          </div>
        )}
      </div>

      <div className="card tight">
        <div className="faint" style={{ marginBottom: 6 }}>
          Прогноз
        </div>
        <ForecastLine data={data} />
      </div>

      <Flags flags={data.flags} />
    </>
  )
}

/**
 * Строка прогноза.
 *
 * Три разных случая, и их нельзя схлопывать:
 *   - прогноза нет как объекта — по движению не поставлена цель, прогнозировать
 *     не к чему (см. progress_exercise: forecast появляется только с целью);
 *   - has_forecast = false — данных мало или R² ниже порога. Показываем
 *     причину сервера ТЕКСТОМ и никакой даты (раздел 5.5, раздел 13);
 *   - has_forecast = true — дата, темп и R² как есть.
 */
function ForecastLine({ data }: { data: ProgressData }) {
  const forecast = data.forecast

  if (!forecast) {
    return (
      <div className="muted">
        По этому движению не поставлена цель, поэтому прогнозировать нечего. Цель ставится на
        экране «Цели».
      </div>
    )
  }

  if (!forecast.has_forecast || !forecast.projected_date) {
    return (
      <>
        <div className="muted">{forecast.reason}</div>
        <div className="faint" style={{ marginTop: 6 }}>
          Дату не показываю: на таких данных она была бы выдумкой.
        </div>
      </>
    )
  }

  return (
    <div>
      <div style={{ fontSize: 17 }}>
        Выход на <b className="tnum">{formatWeight(forecast.target)} кг</b> ориентировочно{' '}
        <b>{formatForecastDate(forecast.projected_date)}</b>
      </div>
      <div className="faint tnum" style={{ marginTop: 4 }}>
        {forecast.rate_kg_per_month !== null && <>темп {formatRate(forecast.rate_kg_per_month)} кг/мес · </>}
        R² = {forecast.r_squared.toFixed(2)} · точек: {forecast.n_points}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Недельный объём                                                           */
/* ------------------------------------------------------------------------- */

function VolumeBlock({ report }: { report: VolumeReportFull }) {
  const week = report.weeks.at(-1)

  if (!week) {
    return (
      <Empty
        title="Объём ещё не набран"
        hint="Запиши первую тренировку — здесь появятся подходы по мышечным группам за неделю."
      />
    )
  }

  // Порядок — по убыванию объёма: перегруз и недобор видно сверху и снизу.
  // Сортировка не меняет цифры, считает по-прежнему сервер.
  const rows = Object.entries(week.volume).sort((a, b) => b[1] - a[1])

  // Кто вышел за коридор — РЕШИЛ СЕРВЕР. Сравнивать цифру с границами здесь
  // нельзя, даже имея обе границы под рукой: на разгрузочной неделе движок
  // недобор флагом сознательно не считает (раздел 5.4, `check_volume`), и
  // клиентская проверка красила бы предупреждением ровно те строки, про
  // которые тот же экран ниже пишет «это замысел, а не недобор».
  const flagged = new Set(
    week.flags
      .filter((f) => f.code === 'volume_below_target' || f.code === 'volume_above_target')
      .map((f) => f.muscle_group)
      .filter((group): group is string => typeof group === 'string'),
  )
  // Масштаб столбика — это геометрия картинки, а не показатель.
  const scale = Math.max(report.target_max, ...rows.map(([, sets]) => sets), 1)
  const zoneLeft = (report.target_min / scale) * 100
  const zoneWidth = ((report.target_max - report.target_min) / scale) * 100

  // Группы, у которых норматив вообще есть. Коридор 10-20 задан разделом 5.4
  // для перечисленных им десяти; предплечья пришли вместе с личными
  // движениями, и цели у них нет. Нарисовать зелёную зону за такой строкой
  // значило бы предъявить картинкой ровно то утверждение о недоборе, которое
  // сервер сознательно НЕ выдал флагом: ошибки нет, а ложь нарисована.
  const withCorridor = new Set(report.corridor_groups)

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16 }}>Неделя с {weekStartLabel(week.week_start)}</div>
          <div className="faint tnum">
            коридор {formatNumber(report.target_min)}–{formatNumber(report.target_max)} подходов
          </div>
          {rows.some(([group]) => !withCorridor.has(group)) && (
            <div className="faint">
              Группы без зелёной зоны движок не нормирует: коридор задан не для них.
            </div>
          )}
        </div>
        {week.is_deload && <span className="pill accent">разгрузка</span>}
      </div>

      {rows.length === 0 ? (
        <div className="muted">На этой неделе рабочих подходов ещё не было.</div>
      ) : (
        rows.map(([group, sets]) => {
          const outside = flagged.has(group)
          const scored = withCorridor.has(group)
          return (
            <div className="vrow" key={group}>
              <div className="vname">{MUSCLE_RU[group] ?? group}</div>
              <div className="vtrack">
                {scored && (
                  <div className="vzone" style={{ left: `${zoneLeft}%`, width: `${zoneWidth}%` }} />
                )}
                <div
                  className={outside ? 'vfill out' : 'vfill'}
                  style={{ width: `${(sets / scale) * 100}%` }}
                />
                {/* Границы коридора рисуются ПОВЕРХ столбика: при заметном
                    перегрузе столбик закрыл бы зелёную зону целиком, и было
                    бы непонятно, относительно чего человек вышел за рамку. */}
                {scored && (
                  <>
                    <div className="vedge" style={{ left: `${zoneLeft}%` }} />
                    <div
                      className="vedge"
                      style={{ left: `calc(${zoneLeft + zoneWidth}% - 2px)` }}
                    />
                  </>
                )}
              </div>
              <span className={outside ? 'pill warn tnum' : 'pill tnum'}>{formatNumber(sets)}</span>
            </div>
          )
        })
      )}

      {week.is_deload && (
        <div className="faint" style={{ marginTop: 10 }}>
          На разгрузочной неделе объём урезан планом — это замысел, а не недобор.
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <Flags flags={week.flags} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* Вес тела                                                                  */
/* ------------------------------------------------------------------------- */

function BodyweightBlock({ report }: { report: BodyweightReport }) {
  if (report.n_points === 0 && report.points.length === 0) {
    return (
      <Empty
        title="Взвешиваний пока нет"
        hint="Вес приходит сюда автоматизацией Shortcuts на iPhone — она раз в день отправляет цифру из Здоровья."
      />
    )
  }

  return (
    <div className="card">
      <div className="row between" style={{ alignItems: 'flex-end' }}>
        <div>
          <div className="faint">среднее за {report.smoothing_days} дн.</div>
          <div className="big-number">
            {formatWeight(report.current_avg)}
            <span style={{ fontSize: 18, fontWeight: 600 }}> кг</span>
          </div>
        </div>
        <div style={{ textAlign: 'right', maxWidth: 170 }}>
          {report.has_trend ? (
            <>
              <div className="faint">темп</div>
              <div className="tnum" style={{ fontSize: 19, fontWeight: 600 }}>
                {formatRate(report.rate_kg_per_week)} кг/нед
              </div>
              <div className="faint tnum">{formatRate(report.rate_pct_per_week)} %/нед</div>
            </>
          ) : (
            // Ноль в rate_* значит «неизвестно», а не «вес стоит на месте».
            // Показать его цифрой — соврать (раздел 2, принцип 3).
            <div className="faint">Данных мало — темп не показываю</div>
          )}
        </div>
      </div>

      {report.points.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Chart
            points={report.points.map((point) => ({ date: point.date, value: point.weight_kg }))}
            height={150}
          />
        </div>
      )}

      <div className="faint tnum" style={{ marginTop: 4 }}>
        взвешиваний: {report.n_points} за {report.span_days} дн., ряд сглажен сервером
      </div>

      <div style={{ marginTop: 12 }}>
        <Flags flags={report.flags} />
      </div>
    </div>
  )
}
